// NewCaseModal — "+ New Case" modal (Phase 3C).
//
// Two cards: Deposit Review | Comparison. Selecting one calls
// POST /api/v1/cases with the right type, then the parent opens the new
// case in the right pane.

import { useState } from 'react';
import { FileText, GitCompare, X, Loader2 } from 'lucide-react';
import { createCase, type Case, type CaseType } from '../api/cases';

interface NewCaseModalProps {
  onClose: () => void;
  onCreated: (c: Case) => void;
}

export default function NewCaseModal({ onClose, onCreated }: NewCaseModalProps) {
  const [creating, setCreating] = useState<CaseType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = (type: CaseType) => {
    setCreating(type);
    setError(null);
    // No hardcoded title: leave it empty so the sidebar shows the unique
    // case_number (e.g. DR-2026-AB12CD) until documents are dropped and a
    // title is auto-derived from the extracted issuer/depositor name.
    // The title is editable inline in the case header at any time.
    createCase({ type, title: '', entity_name: '' })
      .then((c) => onCreated({ ...c, type }))
      .catch((e) => {
        setError(e?.response?.data?.detail || 'Failed to create case');
        setCreating(null);
      });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="new-case-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">What would you like to do?</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          {/* Deposit Review */}
          <button
            data-testid="new-case-deposit"
            onClick={() => start('deposit_review')}
            disabled={!!creating}
            className="card card-hover p-5 text-left flex flex-col gap-3 disabled:opacity-50"
          >
            <FileText size={28} className="text-primary-600" />
            <div>
              <div className="font-semibold text-slate-900">Deposit Review</div>
              <div className="text-sm text-slate-500 mt-1">
                Upload deposit documents and get an automatic deficiency list.
              </div>
            </div>
          </button>
          {/* Comparison */}
          <button
            data-testid="new-case-comparison"
            onClick={() => start('comparison')}
            disabled={!!creating}
            className="card card-hover p-5 text-left flex flex-col gap-3 disabled:opacity-50"
          >
            <GitCompare size={28} className="text-violet-600" />
            <div>
              <div className="font-semibold text-slate-900">Comparison</div>
              <div className="text-sm text-slate-500 mt-1">
                Upload an original and a revised document to see what changed.
              </div>
            </div>
          </button>
        </div>
        {error && <div className="px-6 pb-3 text-sm text-red-600">{error}</div>}
        {creating && (
          <div className="px-6 pb-4 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 size={14} className="animate-spin" /> Creating case...
          </div>
        )}
      </div>
    </div>
  );
}